import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Switch } from "../ui/switch";
import { Badge } from "../ui/badge";
import { Globe, Save, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERMIT_TYPES = [
  { value: "", label: "— Select —" },
  { value: "citizen", label: "Canadian citizen / PR" },
  { value: "open_work_permit", label: "Open Work Permit" },
  { value: "LMIA", label: "LMIA-based work permit" },
  { value: "TFWP", label: "TFWP (Temp Foreign Worker)" },
  { value: "IMP", label: "IMP (International Mobility)" },
  { value: "PGWP", label: "PGWP (Post-Graduate)" },
  { value: "student", label: "Study permit + work" },
  { value: "other", label: "Other" },
];

const STATUSES = [
  { value: "active", label: "Active" },
  { value: "pending_renewal", label: "Pending renewal" },
  { value: "expired", label: "Expired" },
  { value: "n_a", label: "Not applicable" },
];

const blank = (emp_id) => ({
  employee_id: emp_id, permit_type: "", permit_number: "",
  issue_date: "", expiry_date: "", lmia_number: "", lmia_expiry: "",
  status: "active", employer_compliance_notes: "", sponsoring_employer: "",
  notify_admin: true, notify_employee: false, reminder_lead_days: 60,
});

export default function ForeignWorkerTab({ employeeId }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/foreign-workers/by-employee/${employeeId}`);
        setProfile(r.data || blank(employeeId));
      } catch { setProfile(blank(employeeId)); }
      setLoading(false);
    })();
  }, [employeeId]);

  const set = (patch) => { setProfile({ ...profile, ...patch }); setDirty(true); };

  const save = async () => {
    try {
      if (profile.id) {
        await axios.put(`${API}/foreign-workers/${profile.id}`, profile);
      } else {
        const r = await axios.post(`${API}/foreign-workers`, profile);
        setProfile(r.data);
      }
      toast.success("Saved");
      setDirty(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  if (loading) return <p className="text-muted-foreground">Loading…</p>;

  const expiringDays = profile.days_until_expiry;
  const expiringSoon = expiringDays !== null && expiringDays !== undefined && expiringDays <= profile.reminder_lead_days;

  return (
    <Card data-testid="foreign-worker-tab">
      <CardHeader>
        <div className="flex justify-between items-center">
          <div>
            <CardTitle className="flex items-center gap-2"><Globe className="h-5 w-5" /> Foreign Worker / Work Permit</CardTitle>
            <CardDescription>Track work permits, LMIA, and compliance paperwork. Wires into the reminder engine.</CardDescription>
          </div>
          {expiringSoon && <Badge variant="destructive" className="ml-2"><AlertTriangle className="h-3 w-3 mr-1" />Expiring in {expiringDays}d</Badge>}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Permit type</Label>
            <Select value={profile.permit_type || ""} onValueChange={(v) => set({ permit_type: v })}>
              <SelectTrigger data-testid="permit-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>{PERMIT_TYPES.map((p) => <SelectItem key={p.value} value={p.value || "_blank"}>{p.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Status</Label>
            <Select value={profile.status} onValueChange={(v) => set({ status: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Permit number</Label><Input value={profile.permit_number || ""} onChange={(e) => set({ permit_number: e.target.value })} data-testid="permit-number-input" /></div>
          <div><Label>Sponsoring employer</Label><Input value={profile.sponsoring_employer || ""} onChange={(e) => set({ sponsoring_employer: e.target.value })} /></div>
          <div><Label>Issue date</Label><Input type="date" value={profile.issue_date || ""} onChange={(e) => set({ issue_date: e.target.value })} /></div>
          <div><Label>Expiry date</Label><Input type="date" value={profile.expiry_date || ""} onChange={(e) => set({ expiry_date: e.target.value })} data-testid="permit-expiry-input" /></div>
          <div><Label>LMIA number (if applicable)</Label><Input value={profile.lmia_number || ""} onChange={(e) => set({ lmia_number: e.target.value })} /></div>
          <div><Label>LMIA expiry</Label><Input type="date" value={profile.lmia_expiry || ""} onChange={(e) => set({ lmia_expiry: e.target.value })} /></div>
        </div>
        <div>
          <Label>Employer compliance notes</Label>
          <Textarea rows={3} value={profile.employer_compliance_notes || ""} onChange={(e) => set({ employer_compliance_notes: e.target.value })} />
        </div>
        <div className="rounded-lg border p-3 space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Reminder routing</p>
          <label className="flex items-center gap-2 cursor-pointer text-sm"><Switch checked={profile.notify_admin} onCheckedChange={(v) => set({ notify_admin: v })} data-testid="notify-admin-switch" /> Notify admin before expiry</label>
          <label className="flex items-center gap-2 cursor-pointer text-sm"><Switch checked={profile.notify_employee} onCheckedChange={(v) => set({ notify_employee: v })} data-testid="notify-employee-switch" /> Also notify the employee</label>
          <div className="flex items-center gap-2 text-sm">
            <Label className="w-40">Lead time (days)</Label>
            <Input type="number" value={profile.reminder_lead_days} onChange={(e) => set({ reminder_lead_days: parseInt(e.target.value || 60) })} className="w-32" />
          </div>
        </div>
        <Button onClick={save} disabled={!dirty} data-testid="save-foreign-worker-btn"><Save className="h-4 w-4 mr-1" /> Save</Button>
      </CardContent>
    </Card>
  );
}
